/**
 * Device / display-mode checks for the admin PWA. All defensive: every one of
 * these reads browser APIs that may be missing (jsdom, old Safari, SSR-ish
 * test setups) and must answer `false` rather than throw.
 */

interface NavigatorWithExtras extends Navigator {
  /** iOS Safari: true when launched from the home screen. */
  standalone?: boolean;
  /** Chromium User-Agent Client Hints. */
  userAgentData?: { mobile?: boolean };
}

const nav = (): NavigatorWithExtras | null =>
  typeof navigator === 'undefined' ? null : (navigator as NavigatorWithExtras);

const matches = (query: string): boolean => {
  try {
    return typeof window !== 'undefined' && window.matchMedia?.(query).matches === true;
  } catch {
    return false;
  }
};

/** Running as the installed app (home-screen icon), not in a browser tab. */
export const isStandaloneApp = (): boolean =>
  matches('(display-mode: standalone)') || nav()?.standalone === true;

/**
 * A phone - iPhone, or an Android phone (Android tablets omit "Mobile" from
 * their user agent and count as desktop-class, like iPads do).
 */
export const isPhoneDevice = (): boolean => {
  const n = nav();
  if (!n) return false;
  if (n.userAgentData?.mobile === true) return true;
  const ua = n.userAgent ?? '';
  return /iPhone|iPod/.test(ua) || (/Android/.test(ua) && /Mobile/.test(ua));
};

/** iPhone / iPad / iPod, including iPadOS reporting itself as a Mac. */
export const isAppleMobile = (): boolean => {
  const n = nav();
  if (!n) return false;
  const ua = n.userAgent ?? '';
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (n.maxTouchPoints ?? 0) > 1);
};

export type DeviceKind = 'phone' | 'tablet' | 'desktop';

export interface UserAgentInfo {
  /** "iPhone", "Android phone", "Windows", "Mac"... */
  os: string;
  /** "Chrome", "Safari"... or null when unrecognised. */
  browser: string | null;
  kind: DeviceKind;
}

/**
 * Parse a User-Agent string - this browser's, or one the server stored for a
 * signed-in session. `maxTouchPoints` only helps for THIS device (an iPad on
 * iPadOS claims to be a Mac); a stored UA can't tell the two apart.
 */
export const parseUserAgent = (ua: string, maxTouchPoints = 0): UserAgentInfo => {
  const ipad = /iPad/.test(ua) || (/Macintosh/.test(ua) && maxTouchPoints > 1);
  const [os, kind]: [string, DeviceKind] = /iPhone|iPod/.test(ua)
    ? ['iPhone', 'phone']
    : ipad
      ? ['iPad', 'tablet']
      : /Android/.test(ua)
        ? /Mobile/.test(ua)
          ? ['Android phone', 'phone']
          : ['Android tablet', 'tablet']
        : /Windows/.test(ua)
          ? ['Windows', 'desktop']
          : /Macintosh|Mac OS X/.test(ua)
            ? ['Mac', 'desktop']
            : /CrOS/.test(ua)
              ? ['Chromebook', 'desktop']
              : /Linux/.test(ua)
                ? ['Linux', 'desktop']
                : ['Device', 'desktop'];
  const browser = /Edg(e|A|iOS)?\//.test(ua)
    ? 'Edge'
    : /SamsungBrowser/.test(ua)
      ? 'Samsung Internet'
      : /OPR\/|Opera/.test(ua)
        ? 'Opera'
        : /Firefox|FxiOS/.test(ua)
          ? 'Firefox'
          : /Chrome|CriOS/.test(ua)
            ? 'Chrome'
            : /Safari/.test(ua)
              ? 'Safari'
              : null;
  return { os, browser, kind };
};

/** Short label for the Settings device list, e.g. "iPhone" or "Windows · Chrome". */
export const describeThisDevice = (): string => {
  const { os, browser } = parseUserAgent(nav()?.userAgent ?? '', nav()?.maxTouchPoints ?? 0);
  const label = isStandaloneApp() ? `${os} · App` : browser ? `${os} · ${browser}` : os;
  return label.slice(0, 80);
};
